// Datalayer Phase 3 — net-new click events via jQuery event delegation.
//
// Spec source: CanaraHSBC Corporate Website DL-Requirements.xlsx
//
// PRINCIPLE: this file only ADDS events that currently have ZERO callers in
// the codebase. It deliberately does NOT rename, replace, or remove any of
// the existing createDataLayer(...) pushes — Adobe Launch rules and GTM
// triggers configured by the analytics team are listening for the exact
// current event-name strings, and any rename would silently break them.
// If an event the spec lists is already fired by an existing component,
// it is intentionally absent from this file (look in the component's
// own JS for the existing handler).
//
// All handlers below are jQuery delegated handlers on document so they
// catch elements rendered after page load (modals, lazy-loaded sections,
// etc.) without needing component-by-component wiring.
//
// Each handler:
//   1. Pulls page/product context from the DOM helpers AEM already sets
//      on the page (the hidden inputs #pageType / #productPlanName /
//      #productCategoryName that page.html renders, and meta name=template).
//   2. Calls window.createDataLayer(eventName, obj) — every variable not
//      explicitly passed is derived inside createDataLayer (Page* from URL,
//      pagepath1..4, UserDevice, ClientID, etc.).
//   3. Wraps the push in try/catch — analytics must never break the page.

import $ from "jquery";

(function wireDataLayerEvents() {
    /**
     * Pulls product / page context the spec asks every click event to carry.
     * AEM renders these as hidden inputs in page.html if the page has them.
     */
    function pageContext() {
        return {
            productName:
                document.getElementById("productPlanName")?.value || "",
            productCategory:
                document.getElementById("productCategoryName")?.value || "",
        };
    }

    /** Safe wrapper — never lets a push error reach the page. */
    function push(eventName, obj) {
        if (typeof window.createDataLayer !== "function") {return;}
        try {
            window.createDataLayer(eventName, obj || {});
        } catch (e) {
            // Swallowed by design — analytics must not break UX.
        }
    }

    /** Trim long button-text to a sensible length for DL variables. */
    function text(el) {
        return $(el).text().replace(/\s+/g, " ").trim().substring(0, 200);
    }

    // ------------------------------------------------------------------
    // PRODUCT BANNER CTAs
    //
    // Spec rows from "Sheet1": when user clicks Check Premium / Know More /
    // Meet Advisor / Buy Now on a product-related banner. We match by the
    // chli_btn classes that PDP / blog / seasonal banners share, plus a
    // text fallback so we catch banner variations that use different
    // markup.
    // ------------------------------------------------------------------
    $(document).on("click",
        ".pdp-banner-btn, .blog_banner--cta button, .blog_banner--cta [class*='chli_btn--'], .pdp-banner-ctabox button, .pdp-banner-ctabox [class*='chli_btn--'], .blog-banner__cta button",
        function () {
            var label = text(this);
            var lower = label.toLowerCase();
            var ctx = pageContext();
            var common = {
                buttonText: label,
                pageSection: "Banner",
                sectionName: "Banner",
                productName: ctx.productName,
                productCategory: ctx.productCategory,
                eventLabel: label,
            };
            if (lower.indexOf("check premium") >= 0) {
                push("CheckPremium_Clicked", common);
            } else if (lower.indexOf("know more") >= 0) {
                push("KnowMore_Clicked", common);
            } else if (
                lower.indexOf("meet advisor") >= 0 ||
                lower.indexOf("meet an advisor") >= 0
            ) {
                push("MeetAdvisor_Clicked", common);
            } else if (
                lower.indexOf("buy now") >= 0 ||
                lower.indexOf("buy online") >= 0
            ) {
                push("ProductBuyNow_Clicked", common);
            } else if (
                lower.indexOf("resume application") >= 0 ||
                lower.indexOf("resume your application") >= 0
            ) {
                push("ResumeApplication_Clicked", common);
            }
            // No `else` — generic banner button clicks are already covered
            // by the existing `buttonClick` event firings elsewhere; we
            // don't want to double-push.
        }
    );

    // ------------------------------------------------------------------
    // PDF / DOCUMENT DOWNLOADS
    //
    // Spec rows: PDFDownlaod (sic, that's the spec's spelling), plus
    // BuyingGuide_download for the specific Buying-Guide CTAs.
    // Download_Product / Download_NonProduct are ALREADY fired elsewhere
    // (audit shows 8 + 10 callers) so we don't touch those.
    // ------------------------------------------------------------------
    $(document).on("click", "a[href$='.pdf'], a[href*='.pdf?']", function () {
        var label = text(this) || $(this).attr("title") || "";
        var href = $(this).attr("href") || "";
        var ctx = pageContext();
        var common = {
            buttonText: label,
            eventLabel: href,
            sectionName: "Download",
            pageSection: $(this).closest("[data-section-name]").data("section-name") || "",
            productName: ctx.productName,
            productCategory: ctx.productCategory,
        };
        // BuyingGuide-specific events get their own dedicated event name
        // per spec; everything else is a generic PDFDownlaod.
        if (
            label.toLowerCase().indexOf("buying guide") >= 0 ||
            href.toLowerCase().indexOf("buying-guide") >= 0
        ) {
            push("BuyingGuide_download", common);
        } else {
            // Spec literally spells it "PDFDownlaod" — preserved verbatim
            // so dashboards filtering on that string match.
            push("PDFDownlaod", common);
        }
    });

    // ------------------------------------------------------------------
    // SUPPORT contact links (mailto / tel)
    // ------------------------------------------------------------------
    $(document).on("click", "a[href^='mailto:']", function () {
        push("Support_email", {
            buttonText: text(this) || $(this).attr("href").replace(/^mailto:/i, ""),
            eventLabel: $(this).attr("href").replace(/^mailto:/i, ""),
            sectionName: "Support",
        });
    });
    $(document).on("click", "a[href^='tel:']", function () {
        push("Support_mob", {
            buttonText: text(this) || $(this).attr("href").replace(/^tel:/i, ""),
            eventLabel: $(this).attr("href").replace(/^tel:/i, ""),
            sectionName: "Support",
        });
    });

    // ------------------------------------------------------------------
    // FAQ accordion expand/collapse + Show More
    //
    // The existing faqAccordion.js component already fires an "Expand"
    // event on first-open and a "buttonClick" event on title clicks. The
    // spec asks for FAQ_expand / FAQ_collapse / FAQ_ShowMore by name. We
    // add those AS WELL so analytics has both event names available.
    // ------------------------------------------------------------------
    $(document).on("click", ".faqAccordion .accordion-button, .faq-accordion .accordion-button", function () {
        var $btn = $(this);
        var titleText = $btn.text().replace(/\s+/g, " ").trim();
        // Bootstrap toggles aria-expanded BEFORE our handler runs in some
        // versions and AFTER in others; treat the post-click state as the
        // truth: aria-expanded="true" means it's now open.
        // Read after a microtask so we see the final state.
        setTimeout(function () {
            var expanded = $btn.attr("aria-expanded") === "true";
            push(expanded ? "FAQ_expand" : "FAQ_collapse", {
                buttonText: titleText,
                question: titleText,
                eventLabel: titleText,
                sectionName: "FAQ",
            });
        }, 0);
    });
    $(document).on("click", ".faq-show-more, .show-more-faq, [data-faq-show-more]", function () {
        push("FAQ_ShowMore", {
            buttonText: text(this),
            sectionName: "FAQ",
        });
    });

    // ------------------------------------------------------------------
    // Generic popup / modal close
    //
    // Bootstrap modals dispatch on .btn-close and on data-bs-dismiss="modal".
    // We listen for both. Per spec the close event is "Popup_close".
    // ------------------------------------------------------------------
    $(document).on("click", "[data-bs-dismiss='modal'], .modal .btn-close, .popup-close, [data-popup-close]", function () {
        var $modal = $(this).closest(".modal, .popup, [role='dialog']");
        var popupName = $modal.attr("id") ||
                        $modal.find("[data-modal-name]").data("modal-name") ||
                        $modal.find(".modal-title").first().text().trim() ||
                        "";
        push("Popup_close", {
            buttonText: popupName,
            eventLabel: popupName,
            sectionName: "Popup",
        });
    });

    // ------------------------------------------------------------------
    // Language switcher
    //
    // Header language selector — covers the global change-language UI.
    // Most CHLI pages render this as a select inside .language_select or
    // anchor links with data-language attributes.
    // ------------------------------------------------------------------
    $(document).on("change", ".language_select select, .language-select select, [data-language-switch] select", function () {
        var $sel = $(this);
        var newLang = $sel.val() || $sel.find("option:selected").text();
        push("ChangeLanguage", {
            buttonText: newLang,
            eventLabel: newLang,
            sectionName: "Header",
        });
    });
    $(document).on("click", "a[data-language], .language-link, .lang-switch a", function () {
        var newLang = $(this).attr("data-language") || $(this).attr("hreflang") || text(this);
        push("ChangeLanguage", {
            buttonText: newLang,
            eventLabel: newLang,
            sectionName: "Header",
        });
    });

    // ------------------------------------------------------------------
    // Generic VideoClick — spec asks for a single "VideoClick" event when
    // a user interacts with a video. The video component already emits
    // fine-grained Video_Play / Video_pause / Video_Play25% etc. We add
    // a coarse "VideoClick" alongside so dashboards listening for either
    // event name receive data.
    // ------------------------------------------------------------------
    $(document).on("click", ".plyr--video, .video-player, video, .youtube-video-wrapper", function () {
        var $vid = $(this).is("video") ? $(this) : $(this).find("video").first();
        var title = $vid.attr("title") ||
                    $(this).closest("[data-video-title]").data("video-title") ||
                    $(this).find("[data-video-title]").data("video-title") ||
                    "";
        push("VideoClick", {
            videoTitle: title,
            buttonText: title,
            eventLabel: title,
            sectionName: "Video",
        });
    });

    // ------------------------------------------------------------------
    // Inquiry feedback submit — submit handler on feedback / inquiry forms.
    // ------------------------------------------------------------------
    $(document).on("submit", "form.inquiry-form, form.feedback-form, form[data-form-type='feedback']", function () {
        push("Inquiry_Feedback_Submit", {
            sectionName: "Form",
            pageSection: "Form",
            eventLabel: $(this).attr("name") || $(this).attr("id") || "",
        });
    });

    // ------------------------------------------------------------------
    // Rating submission
    // ------------------------------------------------------------------
    $(document).on("click", ".rating-submit, [data-rating-submit], .submit-rating", function () {
        var $form = $(this).closest("form, .rating-widget");
        var rating = $form.find("[data-rating-value].selected").data("rating-value") ||
                     $form.find("input[name='rating']:checked").val() ||
                     $form.find(".rating-stars .active").length ||
                     "";
        push("SubmitRating", {
            consumerRating: rating,
            eventLabel: String(rating),
            sectionName: "Rating",
        });
    });

    // ------------------------------------------------------------------
    // Claim assistance & check claim status (Customer Service pages)
    // ------------------------------------------------------------------
    $(document).on("submit", "form.claim-assistance, form[data-form-type='claim-assistance']", function () {
        push("SubmitClaimAssistance", {
            sectionName: "Form",
            pageSection: "Form",
            eventLabel: $(this).attr("id") || "claim-assistance",
        });
    });
    $(document).on("submit", "form.check-claim-status, form[data-form-type='check-claim-status']", function () {
        var ref = $(this).find("input[name='claimRefNumber'], input[name='claimReferenceNumber']").val() || "";
        push("CheckClaimStatus", {
            claimRefNumber: ref,
            sectionName: "Form",
            pageSection: "Form",
        });
    });

    // ------------------------------------------------------------------
    // QUICK LINKS (additive — quickLinks.js currently fires
    // "QuickLinks_<link text>" as a DYNAMIC event name. We also fire the
    // generic "QuickLinkClick" the spec lists so analytics has a single
    // event name to filter on regardless of which link was clicked.)
    // ------------------------------------------------------------------
    $(document).on("click", ".quick__links__inner .generic__links, .quick-links a, [data-quick-link]", function () {
        var label = text(this);
        push("QuickLinkClick", {
            buttonText: label,
            eventLabel: label,
            sectionName: "Quick Links",
            pageSection: "Side Sticky",
        });
    });

    // ------------------------------------------------------------------
    // MENU LINK CLICK (additive — header.js fires TopNav_L1 / L2 / L3 on
    // specific levels. We also fire the generic "MenuLinkClick" the spec
    // lists for any nav-menu link so analytics has a uniform event name.)
    // ------------------------------------------------------------------
    $(document).on("click",
        ".header__nav a, .header__navigation a, .navigation-menu a, .header__menu a, .header-nav a, [data-menu-link]",
        function () {
            var label = text(this);
            push("MenuLinkClick", {
                buttonText: label,
                eventLabel: label,
                sectionName: "Navigation Menu",
                pageSection: "Navigation Menu",
            });
        }
    );

    // ------------------------------------------------------------------
    // MENU BANNER CLICK (header banner area — content panels inside the
    // mega-menu / dropdown that aren't ordinary nav links).
    // ------------------------------------------------------------------
    $(document).on("click",
        ".header__menu-banner, .header__nav-banner, .nav__dropdown .banner, [data-menu-banner]",
        function () {
            var label = text(this) || $(this).attr("aria-label") || "";
            push("MenuBannerClick", {
                buttonText: label,
                bannerName: label,
                eventLabel: label,
                sectionName: "Navigation Menu - Banner",
                pageSection: "Navigation Menu - Banner",
            });
        }
    );

    // ------------------------------------------------------------------
    // PAY PREMIUM — explicit hook for any link/button that resolves to
    // the pay-premium flow. Matches by URL (.../pay-premium) or by a
    // pay-premium-specific class.
    // ------------------------------------------------------------------
    $(document).on("click",
        "a[href*='pay-premium'], a[href*='paypremium'], .pay-premium-btn, [data-action='pay-premium']",
        function () {
            push("PayPremium", {
                buttonText: text(this),
                eventLabel: text(this),
                sectionName: "Customer Service",
                pageSection: "Pay Premium",
            });
        }
    );

    // ------------------------------------------------------------------
    // LINK / TEXT-LINK / SECTION-LINK CLICK
    //
    // These three spec events have heavy overlap with the existing
    // `buttonClick` event (which fires on most clicks across the site).
    // We add them ALONGSIDE per the "do not break existing events"
    // constraint — both event names fire, analytics team filters as
    // needed at the consumer side.
    //
    //  LinkClick        — <a> element click anywhere on the page
    //  TextLinkClick    — <a> inside rich-text / body content
    //  SectionLinkClick — <a> inside a content section that's neither
    //                     the header nor the footer (mid-page sections)
    //
    // Anchors with role="button" or href="javascript:" are excluded —
    // those are buttons in disguise and already covered by buttonClick.
    // Anchors inside header / footer / nav are excluded (those have
    // dedicated events: MenuLinkClick, FooterClick, etc.).
    // ------------------------------------------------------------------
    $(document).on("click", "a[href]", function () {
        var $a = $(this);
        var href = $a.attr("href") || "";
        // Skip anchors that are clearly buttons (role=button), no-op
        // (javascript: / #), or have already-handled selectors above.
        if (
            $a.attr("role") === "button" ||
            href.indexOf("javascript:") === 0 ||
            href === "" ||
            href === "#"
        ) {
            return;
        }
        // Skip if this link is already covered by a more specific
        // dedicated event (header menu, footer, quick-links, pay-premium,
        // mailto/tel, pdf, hero banner CTA) — those handlers ran already,
        // no need to also fire a generic LinkClick / SectionLinkClick.
        if (
            $a.closest(".header, header.header, .chli-header, .headerCLP").length ||
            $a.closest(".chli-footer, footer.chli-footer, .footer").length ||
            $a.closest(".quick__links__inner, .quick-links").length ||
            $a.closest(".banner__text-cta").length ||
            href.match(/^(mailto:|tel:)/i) ||
            href.match(/\.pdf(\?|$)/i)
        ) {
            return;
        }
        var label = text(this);
        var ctx = pageContext();
        var common = {
            buttonText: label,
            eventLabel: href,
            productName: ctx.productName,
            productCategory: ctx.productCategory,
        };
        // Always fire the generic LinkClick.
        push("LinkClick", Object.assign({}, common, { sectionName: "Link" }));

        // If the anchor is inside an RTE / body-text container, also fire
        // TextLinkClick.
        if ($a.closest(".rte-text, .chlirte, .blog-content, .article-content, .cmp-text").length) {
            push("TextLinkClick", Object.assign({}, common, { sectionName: "Text" }));
        }

        // If the anchor is inside a labelled section, also fire
        // SectionLinkClick. We look for the nearest heading to give
        // analytics the section name.
        var $section = $a.closest("section, [data-section-name]");
        if ($section.length) {
            var sectionName = $section.data("section-name") ||
                              $section.find("h1,h2,h3,h4").first().text().trim() ||
                              $section.attr("aria-label") ||
                              "";
            push("SectionLinkClick", Object.assign({}, common, {
                sectionName: sectionName || "Section",
                pageSection: sectionName || "",
            }));
        }
    });

    // ------------------------------------------------------------------
    // USER LOGIN / REGISTRATION
    //
    // We don't have direct hooks into AEM's auth flow, so we fire on
    // form submit (best-effort). If a server-side success callback
    // becomes available later, the events should be moved into that
    // callback. The current behaviour at least records the ATTEMPT;
    // analytics can correlate against backend logs to confirm success.
    //
    // User_Login          — login form submitted
    // Login_success       — same submit, fired alongside (per spec it's
    //                       supposed to be "on successful login" — we
    //                       can't detect that client-side reliably, so
    //                       this is best-effort; expect some over-count
    //                       from failed-login attempts)
    // User_Registration   — registration form submitted
    // User_Registration_Success — same submit, fired alongside (same caveat)
    // ------------------------------------------------------------------
    $(document).on("submit",
        "form#login-form, form.login-form, form[data-form-type='login'], form[name='login']",
        function () {
            var $form = $(this);
            var common = {
                eventLabel: "Login",
                sectionName: "Login",
                pageSection: "Form",
            };
            push("User_Login", common);
            // Per spec, Login_success fires on successful login. We can't
            // detect server-side success client-side; fire alongside.
            push("Login_success", common);
        }
    );
    $(document).on("submit",
        "form#registration-form, form.registration-form, form[data-form-type='registration'], form[name='registration'], form[name='signup']",
        function () {
            var common = {
                eventLabel: "Registration",
                sectionName: "Registration",
                pageSection: "Form",
            };
            push("User_Registration", common);
            push("User_Registration_Success", common);
        }
    );

    // ------------------------------------------------------------------
    // NOTIFICATION POPUP — generic open / close / dismiss handlers.
    //
    // Notification popups follow the .notification-popup / .promo-popup
    // / .toast-popup naming on the site. We fire NotificationPopup_Click
    // for any click inside the popup body (excluding the close button)
    // and NotificationPopup__X (spec's literal "__X" naming) for the
    // close / X-icon dismiss.
    // ------------------------------------------------------------------
    $(document).on("click",
        ".notification-popup .btn-close, .notification-popup .popup-close, .promo-popup .btn-close, .promo-popup .popup-close, [data-notification-popup-close]",
        function () {
            var $popup = $(this).closest(".notification-popup, .promo-popup, [data-notification-popup]");
            push("NotificationPopup__X", {
                buttonText: $popup.attr("data-popup-name") || $popup.find(".popup-title, .modal-title").first().text().trim() || "",
                eventLabel: "Close",
                sectionName: "Notification Popup",
            });
        }
    );
    $(document).on("click",
        ".notification-popup a, .notification-popup .cta, .promo-popup a, .promo-popup .cta, [data-notification-popup] a, [data-notification-popup] .cta",
        function () {
            var $popup = $(this).closest(".notification-popup, .promo-popup, [data-notification-popup]");
            push("NotificationPopup_Click", {
                buttonText: text(this),
                eventLabel: text(this),
                bannerName: $popup.attr("data-popup-name") || "",
                sectionName: "Notification Popup",
            });
        }
    );
})();

// Marker export so webpack treats this as an ES module and doesn't tree-shake
// the side-effectful IIFE when imported via the common/**/*.js glob in prod.
// Same defensive pattern as csp-inline-style.js and landed-event.js.
export {};
